import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Shield, Users, DollarSign, Heart, MessageCircle, TrendingUp, LogOut } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const adminRes = await axios.get(`${API}/admin/me`, { withCredentials: true });
      setAdmin(adminRes.data);

      const statsRes = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
              <p className="text-sm text-gray-400">Welcome, {admin?.username}</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin/users')}
              className="bg-gray-700 hover:bg-gray-600 text-white"
            >
              Manage Users
            </Button>
            <Button
              onClick={() => navigate('/admin/subscriptions')}
              className="bg-gray-700 hover:bg-gray-600 text-white"
            >
              Subscriptions
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-gray-400 hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Stats Grid */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <h2 className="text-2xl font-bold text-white mb-8">Platform Overview</h2>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {/* Total Users */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <Users className="w-8 h-8 text-blue-500" />
              <span className="text-sm text-gray-400">Total</span>
            </div>
            <div className="text-3xl font-bold text-white mb-1">{stats?.total_users || 0}</div>
            <div className="text-sm text-gray-400">Total Users</div>
            <div className="mt-2 text-xs text-green-500">+{stats?.recent_signups || 0} this week</div>
          </div>

          {/* Verified Users */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <Shield className="w-8 h-8 text-green-500" />
              <span className="text-sm text-gray-400">Verified</span>
            </div>
            <div className="text-3xl font-bold text-white mb-1">{stats?.verified_users || 0}</div>
            <div className="text-sm text-gray-400">Verified Professionals</div>
            <div className="mt-2 text-xs text-gray-500">
              {stats?.total_users ? Math.round((stats.verified_users / stats.total_users) * 100) : 0}% of users
            </div>
          </div>

          {/* Active Subscriptions */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <DollarSign className="w-8 h-8 text-yellow-500" />
              <span className="text-sm text-gray-400">Active</span>
            </div>
            <div className="text-3xl font-bold text-white mb-1">{stats?.active_subscriptions || 0}</div>
            <div className="text-sm text-gray-400">Active Subscriptions</div>
            <div className="mt-2 text-xs text-gray-500">
              ${stats?.total_revenue?.toFixed(2) || '0.00'} total revenue
            </div>
          </div>

          {/* Total Matches */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <Heart className="w-8 h-8 text-pink-500" fill="currentColor" />
              <span className="text-sm text-gray-400">Connections</span>
            </div>
            <div className="text-3xl font-bold text-white mb-1">{stats?.total_matches || 0}</div>
            <div className="text-sm text-gray-400">Total Matches</div>
          </div>

          {/* Total Messages */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <MessageCircle className="w-8 h-8 text-purple-500" />
              <span className="text-sm text-gray-400">Engagement</span>
            </div>
            <div className="text-3xl font-bold text-white mb-1">{stats?.total_messages || 0}</div>
            <div className="text-sm text-gray-400">Messages Sent</div>
          </div>

          {/* Growth Rate */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="w-8 h-8 text-cyan-500" />
              <span className="text-sm text-gray-400">Growth</span>
            </div>
            <div className="text-3xl font-bold text-white mb-1">
              {stats?.total_users && stats?.recent_signups ? 
                Math.round((stats.recent_signups / stats.total_users) * 100) : 0}%
            </div>
            <div className="text-sm text-gray-400">Weekly Growth</div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Button
              onClick={() => navigate('/admin/users')}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white py-4"
            >
              <Users className="w-5 h-5 mr-2" />
              Manage Users
            </Button>
            <Button
              onClick={() => navigate('/admin/subscriptions')}
              className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white py-4"
            >
              <DollarSign className="w-5 h-5 mr-2" />
              View Subscriptions
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
